import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from './LoadingSpinner';

/**
 * Route guards for the app shell and the forced-password-reset screen.
 *
 * Kept in a standalone module (rather than inline in router.tsx) so the forced
 * reset behaviour can be unit-tested without importing the whole route tree.
 */

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <LoadingSpinner size="lg" />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // A support-login session MUST set a new password before entering the app.
  // Authoritative guard: any attempt to load /app (deep link, refresh, history
  // navigation) is bounced to the reset screen until the flag clears.
  if (user?.must_reset_password) return <Navigate to="/reset-password" replace />;
  return <>{children}</>;
}

export function RequireResetRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <LoadingSpinner size="lg" />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Nothing to reset → the reset screen must not be reachable.
  if (!user?.must_reset_password) return <Navigate to="/app" replace />;
  return <>{children}</>;
}
